import React, { useState } from 'react';
import type { PendingAction } from '../services/api/agent/types';

interface ConfirmationDialogProps {
  action: PendingAction;
  onConfirm: (confirmationCode?: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  action,
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const [confirmationCode, setConfirmationCode] = useState('');
  const isDoubleConfirm = action.confirmationType === 'double';

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: '20px'
  };

  const dialogStyle: React.CSSProperties = {
    backgroundColor: '#2B3B44',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px'
  };

  const iconContainerStyle: React.CSSProperties = {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: isDoubleConfirm ? 'rgba(231, 76, 60, 0.2)' : 'rgba(51, 170, 64, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#ffffff',
    margin: 0
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
    margin: 0
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.85)',
    lineHeight: 1.5,
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px'
  };

  const detailsStyle: React.CSSProperties = {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    maxHeight: '150px',
    overflow: 'auto'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
    marginBottom: '16px',
    outline: 'none'
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    borderRadius: '8px',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    border: 'none',
    transition: 'all 0.2s ease',
    opacity: isLoading ? 0.6 : 1
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff'
  };

  const confirmButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: isDoubleConfirm ? '#E74C3C' : '#33AA40',
    color: '#ffffff'
  };

  const formatToolName = (name: string): string => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatArguments = (args: Record<string, any>): string => {
    return Object.entries(args)
      .map(([key, value]) => {
        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        const formattedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        return `${formattedKey}: ${formattedValue}`;
      })
      .join('\n');
  };

  const handleConfirm = () => {
    if (isDoubleConfirm && confirmationCode !== 'CONFIRM') {
      return;
    }
    onConfirm(isDoubleConfirm ? confirmationCode : undefined);
  };

  const canConfirm = !isDoubleConfirm || confirmationCode === 'CONFIRM';

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={dialogStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={iconContainerStyle}>
            {isDoubleConfirm ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E74C3C" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#33AA40" strokeWidth="2">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            )}
          </div>
          <div>
            <h3 style={titleStyle}>
              {isDoubleConfirm ? 'Destructive Action' : 'Confirm Action'}
            </h3>
            <p style={subtitleStyle}>{formatToolName(action.tool)}</p>
          </div>
        </div>

        <div style={descriptionStyle}>
          {action.description}
        </div>

        {Object.keys(action.arguments).length > 0 && (
          <div style={detailsStyle}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '11px' }}>
              {formatArguments(action.arguments)}
            </pre>
          </div>
        )}

        {isDoubleConfirm && (
          <>
            <p style={{ fontSize: '12px', color: '#E74C3C', marginBottom: '8px' }}>
              This action cannot be undone. Type CONFIRM to proceed.
            </p>
            <input
              type="text"
              style={inputStyle}
              placeholder="Type CONFIRM"
              value={confirmationCode}
              onChange={e => setConfirmationCode(e.target.value.toUpperCase())}
              disabled={isLoading}
            />
          </>
        )}

        <div style={buttonContainerStyle}>
          <button
            style={cancelButtonStyle}
            onClick={onCancel}
            disabled={isLoading}
            onMouseEnter={e => {
              if (!isLoading) {
                (e.target as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }
            }}
            onMouseLeave={e => {
              (e.target as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            Cancel
          </button>
          <button
            style={{
              ...confirmButtonStyle,
              opacity: canConfirm && !isLoading ? 1 : 0.5,
              cursor: canConfirm && !isLoading ? 'pointer' : 'not-allowed'
            }}
            onClick={handleConfirm}
            disabled={!canConfirm || isLoading}
            onMouseEnter={e => {
              if (canConfirm && !isLoading) {
                (e.target as HTMLElement).style.transform = 'scale(1.02)';
              }
            }}
            onMouseLeave={e => {
              (e.target as HTMLElement).style.transform = 'scale(1)';
            }}
          >
            {isLoading ? 'Processing...' : (isDoubleConfirm ? 'Delete' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
